import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { moeda } from '../util/formato';

/** Centavos → "130,00", que é como se digita. */
function emReais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Lê "130", "130,00" ou "1.130,00" em centavos. `null` quando não é número. */
function lerCentavos(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/g, '');
  if (!limpo) return null;
  const n = Number(
    limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo,
  );
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * O valor pago, editável na própria célula.
 *
 * Conferir um mês é ler uma lista e corrigir o que estiver errado. Abrir um diálogo
 * para cada correção transforma "arrumar quatro linhas" numa sequência de quatro
 * idas e voltas — e o motivo de estar aqui é justamente conferir várias de uma vez.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CUIDADOS, PORQUE É DINHEIRO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Editar valor apurado no meio de uma tabela é confortável e perigoso na mesma
 * medida. As defesas:
 *
 *  - Só grava se o valor MUDOU. Clicar, olhar e sair não escreve nada.
 *  - `Esc` cancela e devolve o valor original.
 *  - O que sai daqui é uma CORREÇÃO no servidor: a baixa antiga é desfeita e a
 *    nova gravada numa transação só, com registro no histórico do devedor e na
 *    auditoria. Nada acontece em silêncio.
 *  - Recusa do servidor — pagamento já apurado em fechamento, por exemplo —
 *    aparece na própria célula e o valor volta ao que era.
 */
export function ValorPagoEditavel({
  parcelaId,
  valorPago,
  devedorId,
}: {
  parcelaId: string;
  /** Em centavos. */
  valorPago: number;
  devedorId: string;
}) {
  const clienteConsulta = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editando) {
      campo.current?.focus();
      campo.current?.select();
    }
  }, [editando]);

  const corrigir = useMutation({
    mutationFn: (centavos: number) =>
      api.substituir(`/parcelas/${parcelaId}/baixa`, {
        valorPago: (centavos / 100).toFixed(2),
      }),
    onSuccess: () => {
      setErro(null);
      setEditando(false);
      void clienteConsulta.invalidateQueries({ queryKey: ['pagamentos'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
    },
    onError: (e) => {
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível corrigir.');
      setEditando(false);
    },
  });

  function concluir() {
    const centavos = lerCentavos(texto);
    if (centavos === null || centavos <= 0) {
      setErro('Valor inválido.');
      setEditando(false);
      return;
    }
    // Clicar, olhar e sair não escreve nada.
    if (centavos === valorPago) {
      setEditando(false);
      return;
    }
    corrigir.mutate(centavos);
  }

  function aoTeclar(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      concluir();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setTexto(emReais(valorPago));
      setEditando(false);
    }
  }

  if (corrigir.isPending) {
    return <span className="text-xs text-[color:var(--texto-tenue)]">salvando…</span>;
  }

  if (editando) {
    return (
      <input
        ref={campo}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={aoTeclar}
        onBlur={concluir}
        inputMode="decimal"
        className="tabular w-24 rounded border bg-[color:var(--superficie-elevada)] px-2 py-0.5 text-right text-sm text-[color:var(--texto)] focus:border-[color:var(--acento)] focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setTexto(emReais(valorPago));
        setErro(null);
        setEditando(true);
      }}
      title={erro ?? 'Clique para corrigir o valor'}
      className="tabular rounded px-1 text-right hover:bg-[color:var(--superficie-afundada)]"
      style={erro ? { color: 'var(--color-vencido)' } : undefined}
    >
      {moeda(valorPago)}
      {/* A borda pontilhada é o único sinal de que a célula é editável. Sem ela,
          ninguém descobre — e com um botão ao lado a tabela viraria um formulário. */}
      <span
        aria-hidden
        className="ml-1 border-b border-dotted text-[0.6875rem] text-[color:var(--texto-tenue)]"
      >
        editar
      </span>
    </button>
  );
}
